// Card #1795 — the Message mode type: configured text, centred on both
// axes, no data lookup of any kind. `message` is whatever mode.message
// resolved to client-side (never routed through score-api's composeTypedFields
// / `fields`, per this card's own design note and #1798's `fields`-shape
// trap) — ScoreboardContent.jsx only reaches this component once
// boardRenderState.js's classifyBoardRenderState has already confirmed a
// non-empty message, so this component itself doesn't re-derive blank vs
// not-blank. Still defensive about the prop shape (mirrors BlankLayout's
// "never trust the input" contract) since it's cheap and this is a board
// nobody should ever see throw.
//
// AC6 long-message decision (recorded on the card and in the #1795 WIP
// commit): wrap-and-scale-to-a-floor via CSS length-tier buckets keyed off
// message.length against MESSAGE_TEXT_MAX_LENGTH (240, enforced as the
// remote text field's hard maxLength in ModeEditorDialog.jsx), NOT
// continuous JS-measured shrink-to-fit (fragile on kiosk hardware, no
// operator signal) and NOT ellipsis truncation (can hide the actionable
// half of an operational message). Three tiers give the operator a
// predictable "shorter reads bigger" mental model without per-pixel
// measurement; .scoreboard-message__text's max-height + overflow:hidden is
// a defensive floor, not the intended path, and stays true at the tier
// values below (all three fit their real fixtures, see MessageLayout.css).
//
// AC3, SECOND judge denial (corrected 2026-08-21): a prior fix raised the
// short/medium tiers above ScoreHero.css's 10vw hero number, which broke
// AC6 (the raised tiers clipped the card's own example messages, measured,
// not eyeballed). Reverted to the values that measurably fit; AC3's
// comparator is narrowed to other data-bearing PROSE (ClassHeader.css at
// 3.2vw is the largest) instead, since ScoreHero's 10vw is a short fixed
// number, not prose, and is not a fair comparator for a wrapping paragraph.
// Full rationale in MessageLayout.css's tier comments and as a card
// comment. The long/floor tier stays at 4vw (bounded by the 65vh overflow
// box, see MessageLayout.css), still well above every prose comparator but
// below the 10vw hero, at this one near-cap-length tier only, which AC6's
// 240-char cap makes deliberate. The earlier claim that "even the floor
// tier stays by far the largest type on the board" was false (it compared
// against the hero) and stays retracted; the accurate statement is the one
// two sentences up.
import { MESSAGE_TEXT_MAX_LENGTH } from "score-common/constants";
import "./MessageLayout.css";
// Length-tier cutoffs live in their own CSS-free module (not inline here) so
// Cypress specs can import the real constants instead of duplicating them as
// a second hardcoded literal — see messageLengthTiers.js's own header
// comment for why (a spec importing this file directly, which pulls in
// MessageLayout.css, fails the whole spec bundle: Cypress's webpack
// preprocessor has no CSS loader configured, confirmed while fixing #1851).
import { lengthTierClassName, shapeTierClassNames } from "./messageLengthTiers";

import { effectiveMessageLength, messageFitPlan, messageLineUnits, messageSqueeze, resolveMessageLines } from "./messageLines";

// Card #3612: `message` is the text; `messageLines` (optional, absent on every
// mode saved before the card) splits it into lines with a Small / Normal /
// Large size each. The length tier is picked from the lines' weighted length
// (messageLines.js), so a message with no messageLines picks exactly the tier
// it always did and renders as one normal line.
export default function MessageLayout({ message, messageLines }) {
  const lines = resolveMessageLines(message, messageLines);
  const effective = effectiveMessageLength(lines);
  const squeeze = messageSqueeze(effective, MESSAGE_TEXT_MAX_LENGTH);
  // One Normal line (every message saved before #3612, and a new one that
  // uses neither a break nor a size) is the bare text in the box, byte for
  // byte what this layout always rendered (ScoreboardPage.liveModesByteIdentical
  // pins it). Anything else gets a block per line.
  const plain = lines.length === 1 && lines[0].size === "normal" && squeeze === 1;
  // Card #3619: a plain message keeps the character-count tier it always had.
  // Anything else gets, per canvas shape, the largest tier its estimated
  // wrapped rows fit (messageLines.js messageFitPlan), plus a squeeze var when
  // even the floor tier would not fit.
  const plan = plain ? null : messageFitPlan(lines);
  const tierClasses = plain
    ? `${lengthTierClassName(effective, MESSAGE_TEXT_MAX_LENGTH)} ${shapeTierClassNames(effective)}`
    : plan.map(({ suffix, tier }) => `scoreboard-message__text--${suffix ? `${suffix}-` : ""}${tier}`).join(" ");
  const style = plain
    ? undefined
    : {
        "--message-units": messageLineUnits(lines).toFixed(2),
        ...Object.fromEntries(plan.filter(({ squeeze: fit }) => fit < 1).map(({ varName, squeeze: fit }) => [varName, fit.toFixed(3)])),
      };
  return (
    <div className="scoreboard-message" data-testid="scoreboard-message">
      <div className={`scoreboard-message__text ${tierClasses}`} style={style} data-testid="scoreboard-message-text">
        {plain
          ? lines[0].text
          : lines.map((line, i) => (
              <div key={i} className={`scoreboard-message__line scoreboard-message__line--${line.size}`} data-testid="scoreboard-message-line" data-size={line.size}>
                {line.text === "" ? "\u00a0" : line.text}
              </div>
            ))}
      </div>
    </div>
  );
}
